import { Link, useParams } from "react-router-dom";
import { MessageCircle, Instagram, Mail, MapPin } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { imgUrl } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";

export function AboutPage() {
  const { settings } = useStore();
  const custom = (settings?.about_text || "").trim();
  const paragraphs = custom ? custom.split(/\n+/).map((s) => s.trim()).filter(Boolean) : null;
  return (
    <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6" data-testid="about-page">
      <Seo title="Our Story — Anaya Fashion & Gifts" description="The story of Anaya Fashion & Gifts by Afia & Anaya — a boutique of hand-picked vintage watches, jewellery, hampers and little luxuries." path="/about" />
      <Reveal>
        <div className="text-center">
          {settings?.logo_url && <img src={imgUrl(settings.logo_url)} alt="Anaya logo" className="mx-auto h-36 w-36 rounded-full object-cover shadow-xl ring-1 ring-primary/30" />}
          <p className="mt-8 text-[11px] tracking-[0.4em] text-primary uppercase">Our story</p>
          <h1 className="mt-3 font-display text-4xl leading-tight sm:text-5xl">More than <em className="gold-text">just a store</em></h1>
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="glass mt-10 rounded-3xl p-8 text-base font-light leading-relaxed text-muted-foreground sm:p-10" data-testid="about-content">
          {paragraphs ? paragraphs.map((p, i) => <p key={i} className={i ? "mt-4" : ""}>{p}</p>) : (
            <>
              <p>Anaya began as a shared dream between Afia &amp; Anaya — two people who believed gifting should feel personal, not transactional. What started with a small tray of vintage watches has grown into a boutique of seventeen little worlds: jewellery, hampers, accessories and tiny luxuries.</p>
              <p className="mt-4">Every piece is hand-picked, every parcel is wrapped by hand, and every customer is a message away on WhatsApp. Thank you for letting us be a small part of your big moments.</p>
              <p className="mt-6 font-display text-xl italic text-foreground">— Afia &amp; Anaya</p>
            </>
          )}
        </div>
      </Reveal>
    </div>
  );
}

export function ContactPage() {
  const { settings } = useStore();
  const wa = settings?.whatsapp || "919643444088";
  const cards = [
    { icon: MessageCircle, title: "WhatsApp", value: `+${wa}`, href: `https://wa.me/${wa}`, testid: "contact-whatsapp" },
    { icon: Instagram, title: "Instagram", value: "@anaya.shop__", href: settings?.instagram || "https://www.instagram.com/anaya.shop__", testid: "contact-instagram" },
    { icon: Mail, title: "Email", value: settings?.email || "hello@anayafashion.com", href: `mailto:${settings?.email || "hello@anayafashion.com"}`, testid: "contact-email" },
  ];
  return (
    <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6" data-testid="contact-page">
      <Seo title="Contact — Anaya Fashion & Gifts" description="Reach Anaya Fashion & Gifts on WhatsApp or Instagram for orders, custom hampers and support." path="/contact" />
      <Reveal>
        <div className="text-center">
          <p className="text-[11px] tracking-[0.4em] text-primary uppercase">We reply fast</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl">Say hello</h1>
          <p className="mx-auto mt-4 max-w-md text-sm font-light text-muted-foreground">Questions about an order, a product, or a custom gift hamper? Message us — a real person answers.</p>
        </div>
      </Reveal>
      <div className="mt-10 grid gap-4 sm:grid-cols-3">
        {cards.map(({ icon: Icon, title, value, href, testid }, i) => (
          <Reveal key={title} delay={i * 0.08}>
            <a href={href} target="_blank" rel="noreferrer" data-testid={testid}
              className="glass block rounded-3xl p-6 text-center transition-transform duration-300 hover:-translate-y-1">
              <Icon className="mx-auto text-primary" size={26} />
              <p className="mt-3 font-display text-lg">{title}</p>
              <p className="mt-1 break-all text-sm font-light text-muted-foreground">{value}</p>
            </a>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <div className="glass mt-6 flex items-center gap-4 rounded-3xl p-6">
          <MapPin className="shrink-0 text-primary" size={22} />
          <p className="text-sm font-light" data-testid="contact-address">{settings?.address || "Anaya Fashion, New Delhi, India"}</p>
        </div>
      </Reveal>
    </div>
  );
}

const POLICIES = {
  shipping: {
    title: "Shipping Policy",
    body: [
      "We dispatch orders within 1–2 business days of confirmation. Delivery typically takes 2–5 business days across India.",
      "Shipping is free on all orders for now — a little gift from us.",
      "Once your order ships, we'll message your tracking details on the phone number you provided at checkout.",
      "For Cash on Delivery orders, please keep the exact amount ready for our courier partner.",
    ],
  },
  returns: {
    title: "Return & Refund Policy",
    body: [
      "A clear unboxing video is mandatory when opening the parcel.",
      "Returns or replacements are only accepted for damaged, defective, or incorrect products with valid unboxing video proof.",
      "The video must be shared via WhatsApp within 24 hours of delivery.",
      "No unboxing video, no return or replacement.",
      "No returns for personal preferences or change of mind.",
      "Thank you for shopping with Anaya Fashion & Gifts!",
    ],
  },
  privacy: {
    title: "Privacy Policy",
    body: [
      "We only collect the details needed to fulfil your order: your name, phone number and delivery address.",
      "We never sell or share your information with third parties for marketing.",
      "Payment proofs you upload are used solely to verify your payment and are visible only to the store owner.",
      "You can ask us to delete your order details anytime by messaging us on WhatsApp.",
    ],
  },
  terms: {
    title: "Terms of Service",
    body: [
      "All prices are listed in Indian Rupees and are subject to change. Demo prices may apply while our catalogue is being finalised.",
      "Orders paid via UPI or bank transfer are confirmed only after manual payment verification by our team.",
      "Product photos are taken by us; slight colour variation may occur due to lighting and screens.",
      "By placing an order you agree to provide accurate delivery information and to be reachable on the phone number provided.",
    ],
  },
};

export function PolicyPage() {
  const { slug } = useParams();
  const { settings } = useStore();
  const policy = POLICIES[slug];
  if (!policy) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-28 text-center" data-testid="policy-not-found">
        <h1 className="font-display text-4xl">Page not found</h1>
        <Link to="/" className="mt-6 inline-block rounded-full bg-foreground px-7 py-3 text-xs tracking-[0.2em] text-white uppercase hover:bg-primary">Go home</Link>
      </div>
    );
  }
  const customKey = { shipping: "policy_shipping", returns: "policy_return", privacy: "policy_privacy", terms: "policy_terms" }[slug];
  const custom = (settings?.[customKey] || "").trim();
  const body = custom ? custom.split(/\n+/).map((s) => s.trim()).filter(Boolean) : policy.body;
  return (
    <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6" data-testid={`policy-${slug}`}>
      <Seo title={`${policy.title} — Anaya Fashion & Gifts`} description={body[0] ? body[0].slice(0, 158) : policy.title} path={`/policies/${slug}`} />
      <Reveal>
        <p className="text-[11px] tracking-[0.4em] text-primary uppercase">The fine print</p>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl">{policy.title}</h1>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="glass mt-8 space-y-4 rounded-3xl p-8 text-base font-light leading-relaxed text-muted-foreground sm:p-10" data-testid="policy-content">
          {body.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </Reveal>
    </div>
  );
}
